import { useState, useEffect, useCallback, useRef } from "react";
import {
  getConversations,
  getMessages,
  sendMessage,
} from "services/messagesService";

const POLL_INTERVAL = 10_000;
const PAGE_LIMIT = 5;

interface Conversation {
  id: string;
  name?: string;
  displayName: string;
}

export interface ConversationMessage {
  id: string;
  sender: string;
  text: string;
  time: string;
  initials: string;
  avatarVariant: "teal" | "blue";
  out: boolean;
  pending?: boolean;
}

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const getLocalUser = () => {
  try {
    const raw = localStorage.getItem("user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const toMessage = (m: any, currentUserId?: string): ConversationMessage => ({
  id: m.id,
  sender: `${m.author.firstName ?? ""} ${m.author.lastName ?? ""}`.trim(),
  text: m.body,
  time: formatTime(m.createdAt),
  initials: `${m.author.firstName?.[0] ?? ""}${m.author.lastName?.[0] ?? ""}`,
  avatarVariant: m.author.id === currentUserId ? "teal" : "blue",
  out: m.author.id === currentUserId,
});

interface UseConversationOptions {
  projectId: string;
  isActive?: boolean;
  preferConvName?: string;
}

export const useConversation = ({
  projectId,
  isActive,
  preferConvName,
}: UseConversationOptions) => {
  const localUser = getLocalUser();
  const currentUserId: string | undefined = localUser?.id;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState("");
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [tabsLoading, setTabsLoading] = useState(false);
  const [msgsLoading, setMsgsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [sending, setSending] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);
  const activeConvIdRef = useRef("");
  const pageRef = useRef(1);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const clearPoll = () => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const refreshMessages = useCallback(
    (conversationId: string) => {
      getMessages(projectId, conversationId, 1, PAGE_LIMIT).then(
        ({ data }: any) => {
          if (!mountedRef.current || activeConvIdRef.current !== conversationId)
            return;
          setMessages((prev) => {
            const incoming: ConversationMessage[] = (data?.data ?? [])
              .map((m: any) => toMessage(m, currentUserId))
              .reverse();
            const existingNonPending = prev.filter(
              (m) => !m.pending && !m.id.includes("temp"),
            );
            const existingIds = new Set(existingNonPending.map((m) => m.id));
            const newMsgs = incoming.filter((m) => !existingIds.has(m.id));
            // Drop all pending on poll — confirmed msgs arrive via incoming; stale ones are cleared
            return [...existingNonPending, ...newMsgs];
          });
        },
      );
    },
    [projectId, currentUserId],
  );

  const startPolling = useCallback(
    (conversationId: string) => {
      clearPoll();
      pollRef.current = setInterval(() => {
        refreshMessages(conversationId);
      }, POLL_INTERVAL);
    },
    [refreshMessages],
  );

  const fetchMessages = useCallback(
    (conversationId: string) => {
      activeConvIdRef.current = conversationId;
      pageRef.current = 1;
      clearPoll();
      setHasMore(false);
      setMsgsLoading(true);
      getMessages(projectId, conversationId, 1, PAGE_LIMIT)
        .then(({ data }: any) => {
          if (!mountedRef.current) return;
          const incoming = (data?.data ?? [])
            .map((m: any) => toMessage(m, currentUserId))
            .reverse();
          setMessages(incoming);
          setHasMore(incoming.length === PAGE_LIMIT);
        })
        .finally(() => {
          if (!mountedRef.current) return;
          setMsgsLoading(false);
          startPolling(conversationId);
        });
    },
    [projectId, currentUserId, startPolling],
  );

  // Load older messages and prepend — returns true if items were prepended
  const loadMoreMessages = useCallback((): Promise<boolean> => {
    if (loadingMore || !hasMore || !activeConvIdRef.current)
      return Promise.resolve(false);
    const conversationId = activeConvIdRef.current;
    const nextPage = pageRef.current + 1;
    setLoadingMore(true);
    return getMessages(projectId, conversationId, nextPage, PAGE_LIMIT)
      .then(({ data }: any) => {
        if (!mountedRef.current || activeConvIdRef.current !== conversationId)
          return false;
        const incoming: ConversationMessage[] = (data?.data ?? [])
          .map((m: any) => toMessage(m, currentUserId))
          .reverse();
        if (incoming.length === 0) {
          setHasMore(false);
          return false;
        }
        pageRef.current = nextPage;
        setHasMore(incoming.length === PAGE_LIMIT);
        setMessages((prev) => [...incoming, ...prev]);
        return true;
      })
      .finally(() => {
        if (mountedRef.current) setLoadingMore(false);
      });
  }, [loadingMore, hasMore, projectId, currentUserId]);

  useEffect(() => {
    if (!projectId || !isActive) {
      clearPoll();
      return;
    }
    setTabsLoading(true);
    getConversations(projectId)
      .then(({ data }: any) => {
        if (!mountedRef.current) return;
        const convs: Conversation[] = data?.data ?? [];
        setConversations(convs);
        if (convs.length) {
          const preferred = preferConvName
            ? (convs.find((c) => c.name === preferConvName) ?? convs[0])
            : convs[0];
          setActiveId(preferred.id);
          fetchMessages(preferred.id);
        }
      })
      .finally(() => {
        if (mountedRef.current) setTabsLoading(false);
      });

    return () => clearPoll();
  }, [projectId, isActive]);

  const handleTabChange = (id: string) => {
    if (id === activeId) return;
    setActiveId(id);
    setMessages([]);
    fetchMessages(id);
  };

  const handleSend = (input: string, onClear: () => void) => {
    const body = input.trim();
    if (!body || !activeId || sending) return;

    const tempId = `temp-${Date.now()}`;
    const optimistic: ConversationMessage = {
      id: tempId,
      sender: localUser
        ? `${localUser.firstName ?? ""} ${localUser.lastName ?? ""}`.trim()
        : "You",
      text: body,
      time: formatTime(new Date().toISOString()),
      initials: localUser
        ? `${localUser.firstName?.[0] ?? ""}${localUser.lastName?.[0] ?? ""}`
        : "Y",
      avatarVariant: "teal",
      out: true,
      pending: true,
    };

    setMessages((prev) => [...prev, optimistic]);
    onClear();
    setSending(true);

    sendMessage(projectId, activeId, body)
      .then(({ data }: any) => {
        const m = data?.data;

        if (!m) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === tempId ? { ...msg, pending: false } : msg,
            ),
          );
          return;
        }

        const realMsg = {
          ...toMessage(m, currentUserId),
          pending: false,
        };

        setMessages((prev) =>
          prev.map((msg) => (msg.id === tempId ? realMsg : msg)),
        );
      })
      .finally(() => {
        if (mountedRef.current) setSending(false);
      });
  };

  const activeLabel =
    conversations.find((c) => c.id === activeId)?.displayName ?? "";

  return {
    conversations,
    activeId,
    messages,
    tabsLoading,
    msgsLoading,
    loadingMore,
    hasMore,
    sending,
    activeLabel,
    handleTabChange,
    handleSend,
    loadMoreMessages,
  };
};
